import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLAN_META = {
  monthly: { tagline: "Billed as a one-time month", period: "/mo", highlight: false },
  annual: { tagline: "Best value — save 30%", period: "/yr", highlight: true },
};

const PRO_FEATURES = [
  "Two-way calendar sync (Google & Outlook)",
  "AI Smart Drafting (lessons, emails, notes)",
  "Simple student data tracking",
  "All premium pastel themes",
  "Unlimited resource links",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  // Handle return from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          toast.success("Welcome to Pro! Everything is unlocked. 🎉");
          setParams({});
          navigate("/dashboard");
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setParams({});
          return;
        }
      } catch { /* */ }
      if (attempts++ < 6) setTimeout(poll, 2000);
      else { toast.message("Still processing — check back in a moment."); setParams({}); }
    };
    toast.loading("Confirming your payment…", { duration: 2000 });
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async (pkgId) => {
    setLoadingPkg(pkgId);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingPkg(null);
    }
  };

  return (
    <div className="min-h-screen bg-background tcc-dots py-12 px-5">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate(user ? "/dashboard" : "/")} className="flex items-center gap-1.5 text-slate-500 hover:text-primary mb-6 text-sm" data-testid="pricing-back">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/40 text-slate-700 text-sm font-medium mb-4">
            <Crown size={15} className="text-primary" /> Teacher Command Center Pro
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight text-slate-800">Unlock your whole day</h1>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto">The free plan runs your dashboard. Pro adds real calendar sync, AI drafting, student tracking, and every theme.</p>
          {user?.is_pro && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/12 text-primary font-semibold" data-testid="already-pro-badge">
              <Check size={16} /> You're on Pro — thank you!
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {["monthly", "annual"].map((id) => {
            const pkg = packages.find((p) => p.id === id);
            const meta = PLAN_META[id];
            if (!pkg) return null;
            return (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className={`tcc-card p-7 flex flex-col relative ${meta.highlight ? "ring-2 ring-primary" : ""}`}
                data-testid={`plan-${id}`}
              >
                {meta.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold">Most popular</span>}
                <h3 className="font-heading font-bold text-lg text-slate-800">{pkg.label}</h3>
                <p className="text-sm text-slate-400 mt-1">{meta.tagline}</p>
                <div className="my-5">
                  <span className="font-heading text-4xl font-extrabold text-slate-800">${pkg.amount % 1 === 0 ? pkg.amount : pkg.amount.toFixed(2)}</span>
                  <span className="text-slate-400 text-sm">{meta.period}</span>
                </div>
                <ul className="space-y-2.5 mb-7 flex-1">
                  {PRO_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check size={17} className="text-primary shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => checkout(id)}
                  disabled={loadingPkg === id || user?.is_pro}
                  data-testid={`checkout-${id}`}
                  className={`rounded-full h-11 ${meta.highlight ? "bg-primary hover:bg-primary/90" : "bg-slate-800 hover:bg-slate-700"} text-white`}
                >
                  {loadingPkg === id ? <Loader2 className="animate-spin" /> : user?.is_pro ? "Current plan" : <><Sparkles size={16} className="mr-1.5" /> Choose {pkg.label.replace("Pro ", "")}</>}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">Secure checkout by Stripe · Cancel anytime · Test mode uses card 4242 4242 4242 4242</p>
      </div>
    </div>
  );
}
